// Module: BriefScreen
// Role: Full-screen brief with parsed sections + 152-FZ consent checkboxes.
//       FTR-1315: st.9 general consent (processing) records to backend.
//       Consent gate blocks CTA until consented.
// Uses: atoms/BriefSection, atoms/ConsentRow, atoms/Chip, atoms/Caveat,
//       atoms/Frame, atoms/MiniHeader, StepStrip, WizardCtaBar,
//       flow-client:useSubmitCheckpoint,useRecordConsents, flow-types, tokens
// Used by: FlowScreen.tsx (case 'BRIEF')
// Design source: ai/designs/miniapp/project/pantene-flow.jsx:354 (PF_Brief)

import { useState, useCallback } from 'react';
import { Frame } from '../atoms/Frame';
import { MiniHeader } from '../atoms/MiniHeader';
import { Caveat } from '../atoms/Caveat';
import { BriefSection } from '../atoms/BriefSection';
import { ConsentRow } from '../atoms/ConsentRow';
import { WizardCtaBar } from '../../../components/atoms/WizardCtaBar';
import { StepStrip } from '../StepStrip';
import { useSubmitCheckpoint, useRecordConsents } from '../../../api/flow-client';
import { flowStepLabel } from '../_labels';
import { P, S, T } from '../../../styles/tokens';
import type {
  Checkpoint,
  CheckpointAttempt,
  SlotFlowRun,
  RequiredConsents,
} from '../../../api/flow-types';

// ─── Types ───────────────────────────────────────────────────────────────────

interface BriefScreenProps {
  checkpoint: Checkpoint;
  attempt?: CheckpointAttempt;
  flowRun: SlotFlowRun;
  checkpoints: Checkpoint[];
  attempts: CheckpointAttempt[];
  onCta: () => void;
}

// ─── Section parser ──────────────────────────────────────────────────────────

interface ParsedSection {
  title: string;
  body: string;
}

/** Parse markdown brief_content into sections split by `## ` headings. */
function parseBriefSections(content: string): ParsedSection[] {
  const sections: ParsedSection[] = [];
  const lines = content.split('\n');
  let currentTitle = '';
  let currentBody: string[] = [];

  for (const line of lines) {
    if (line.startsWith('## ')) {
      if (currentTitle) {
        sections.push({
          title: currentTitle,
          body: currentBody.join('\n').trim(),
        });
      }
      currentTitle = line.slice(3).trim();
      currentBody = [];
    } else {
      currentBody.push(line);
    }
  }
  if (currentTitle) {
    sections.push({
      title: currentTitle,
      body: currentBody.join('\n').trim(),
    });
  }
  return sections;
}

/** Default section metadata when brief_content has numbered sections. */
const SECTION_META: { emoji: string }[] = [
  { emoji: '🎬' },
  { emoji: '🎯' },
  { emoji: '✓' },
  { emoji: '🚫' },
  { emoji: '⏰' },
  { emoji: '📜' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Extract on_take scopes + version from checkpoint metadata (data-driven). */
function getTakeConsents(
  checkpoint: Checkpoint,
): { scopes: string[]; version: string } | null {
  const meta = checkpoint.metadata as
    | { required_consents?: RequiredConsents }
    | undefined;
  const rc = meta?.required_consents;
  if (rc?.on_take && rc.on_take.length > 0) {
    return { scopes: rc.on_take, version: rc.version ?? '' };
  }
  return null;
}

// Canonical st.9 scopes when checkpoint metadata doesn't declare on_take
const CANONICAL_ST9_SCOPES = ['personal_data', 'video_storage_internal'];
const CANONICAL_ST9_VERSION = '';

// ─── Component ───────────────────────────────────────────────────────────────

export function BriefScreen({
  checkpoint,
  flowRun,
  checkpoints,
}: BriefScreenProps) {
  const [accepted, setAccepted] = useState(false);
  const [consentRecording, setConsentRecording] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);

  const submitCheckpoint = useSubmitCheckpoint(flowRun.slot_id, checkpoint.id);
  const recordConsents = useRecordConsents();

  // FTR-1315: data-driven on_take scopes or canonical st.9 fallback
  const takeConsents = getTakeConsents(checkpoint);
  const consentScopes = takeConsents?.scopes ?? CANONICAL_ST9_SCOPES;
  const consentVersion = takeConsents?.version ?? CANONICAL_ST9_VERSION;

  // FTR-1315: toggle consent and record to backend
  const handleConsentToggle = useCallback(async () => {
    if (accepted) {
      setAccepted(false);
      return;
    }
    setConsentRecording(true);
    setConsentError(null);
    try {
      await recordConsents.mutateAsync({
        scopes: consentScopes,
        version: consentVersion,
      });
      setAccepted(true);
    } catch {
      setConsentError('Не удалось записать согласие. Попробуйте ещё раз.');
    } finally {
      setConsentRecording(false);
    }
  }, [accepted, consentScopes, consentVersion, recordConsents]);

  const handleAccept = () => {
    if (!accepted) return;
    submitCheckpoint.mutate({ artefact_path: '' });
  };

  const briefContent =
    checkpoint.step_context?.brief_content ??
    checkpoint.instructions_text ??
    '';
  const sections = parseBriefSections(briefContent);

  const parentCount = checkpoints.filter(
    (c) => c.parent_checkpoint_id === null,
  ).length;

  const ctaLabel = accepted
    ? 'Принимаю — поехали'
    : 'Отметь согласие, чтобы продолжить';

  return (
    <Frame>
      <MiniHeader title={flowStepLabel(checkpoint.kind)} />

      <div style={{ padding: '12px 16px 0' }}>
        <StepStrip
          current={checkpoint.position + 1}
          total={parentCount}
          showCaption
        />
      </div>

      {/* Scrollable body */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '0 16px 140px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Hero */}
        <div style={{ marginTop: 18 }}>
          <div style={{ font: T.title2, color: S.label, lineHeight: 1.15 }}>
            📜 Бриф кампании
          </div>
          <div style={{ marginTop: 6, position: 'relative' }}>
            <Caveat color={P.terraDark} rot={-2} size={22}>
              читай не спеша — это база
            </Caveat>
          </div>
        </div>

        {/* Parsed sections */}
        {sections.map((section, i) => {
          const meta = SECTION_META[i] ?? { emoji: `${i + 1}` };
          return (
            <BriefSection
              key={i}
              num={String(i + 1)}
              emoji={meta.emoji}
              title={section.title}
            >
              <div
                style={{
                  font: T.footnote,
                  color: S.label,
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {section.body}
              </div>
            </BriefSection>
          );
        })}

        {/* FTR-1315: st.9 consent (general data processing) — separate from st.10.1 */}
        <div style={{ marginTop: 22 }} data-testid="brief-consent-section">
          <div
            style={{
              font: T.caption,
              fontWeight: 700,
              color: S.tertiaryLabel,
              textTransform: 'uppercase' as const,
              letterSpacing: 0.2,
              marginBottom: 12,
            }}
          >
            Согласие на обработку данных
          </div>
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
          >
            <ConsentRow
              checked={accepted}
              onToggle={handleConsentToggle}
              text="Даю согласие на обработку персональных данных (фото, видео, контактные данные) для выполнения задания кампании."
              subtext={
                'Цель: проверка выполнения задания. Срок хранения — 90 дней. ' +
                'Отозвать согласие можно в любой момент, написав в поддержку.'
              }
              footnote={
                consentRecording
                  ? 'Записываем...'
                  : '* Согласно ст.9 152-ФЗ · Политика конфиденциальности'
              }
            />
          </div>
          {consentError && (
            <div
              style={{
                font: T.caption,
                color: '#C0392B',
                marginTop: 6,
              }}
            >
              {consentError}
            </div>
          )}
        </div>

        <div style={{ height: 24 }} />
      </div>

      <WizardCtaBar
        label={ctaLabel}
        onClick={handleAccept}
        disabled={!accepted}
      />
    </Frame>
  );
}
